// React Imports
import { useEffect } from "react";

// Redux Imports
import { useDispatch, useSelector } from "react-redux";
import {
  resetBeneTxnMultiApprovalError,
  resetBeneTxnMultiDeniedApprovalData,
} from "../../../../redux/features/beneficiary/beneTxnViewAccData/beneTxnViewAccData";

// Utils Imports
import { GlobalConfiguration } from "../../../../utils/configurationProvider";
import formatDateViewAll from "../../../../utils/formatDateViewAll";

// Component Imports
import { useNavigate } from "react-router-dom";
import ICONS from "../../../../assets/Icons";
import CardContainer from "../../../_utils/CommonViewAllCard/CardContainer";
import TextWithCTA from "../../../_utils/CommonViewAllCard/CardSubComponent/TextWithCTA";
import TextWithIcon from "../../../_utils/CommonViewAllCard/CardSubComponent/TextWithIcon";

const ReimbursementRequestTxnMultiCard = ({
  filterOpen,
  data,
  setAllAccounts,
  setselectAllCard,
  selectedTransactions,
  setSelectedTransactions,
  setTotalAmount,
  setSelectedSendTransactions,
  selectedSendTransactions,
  SideDrawerOpen,
  openBeneTxnDetailModal,
  setTxnId,
  setDatas,
  filterRef,
}) => {
  // 1️⃣ useDispatch
  const dispatch = useDispatch();

  // 2️⃣ useSelector
  const { BeneTxnMultiApprovalError, BeneTxnMultiDeniedApprovalError } =
    useSelector((state) => state?.beneTxnViewAccData);

  const services = useSelector((state) => state.refreshToken.services);
  const navigate = useNavigate();

  // 3️⃣ Callback / Handler functions
  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "var(--pending)";
      case "approved":
        return "var(--settled)";
      case "denied":
      case "failed":
      case "rejected":
        return "var(--failed)";
      case "abandoned":
        return "var(--abandoned)";
      case "hold":
      case "queued":
        return "var(--queued)";
      default:
        return "var(--settled)";
    }
  };

  const handleCardClick = (account) => {
    if (account.beneficiary_account_approval_status === "approved") {
      setselectAllCard(false);
      setSelectedTransactions((prevSelected) => {
        const cardAmount = Number(account.requested_amount);
        const isSelected = prevSelected.includes(account.request_id);

        if (isSelected) {
          setTotalAmount((prevAmount) => prevAmount - cardAmount);
          const newData = selectedSendTransactions.filter(
            (val) => val?.request_id !== account.request_id,
          );
          setSelectedSendTransactions(newData);
          return prevSelected.filter((id) => id !== account.request_id);
        }
        if (prevSelected.length < GlobalConfiguration.categorymaxLength) {
          setTotalAmount((prevAmount) => prevAmount + cardAmount);
          setSelectedSendTransactions((prev) => [...prev, account]);
          return [...prevSelected, account.request_id];
        }
        return prevSelected;
      });
    }
  };

  const handleCtaClick = (e, account) => {
    const hasAccountApprovalAccess =
      services?.includes("RAAccount") && services.includes("RVAccount");
    e.stopPropagation();
    setDatas(account);

    if (!hasAccountApprovalAccess) {
      navigate("/feature_not_assigned", {
        state: { unavailableService: "RAAccount, RVAccount" },
      });
    }

    if (!sessionStorage.getItem("q2p_token")) {
      return;
    }
    if (
      account.beneficiary_account_approval_status === "pending" ||
      account.beneficiary_account_approval_status === "rejected"
    ) {
      SideDrawerOpen(account?.beneficiary_account_id, account.request_id);
    } else {
      openBeneTxnDetailModal();
      setTxnId(account.request_id);
    }
  };

  // 4️⃣ useEffect
  useEffect(() => {
    dispatch(resetBeneTxnMultiApprovalError());
    dispatch(resetBeneTxnMultiDeniedApprovalData());
  }, [BeneTxnMultiApprovalError, BeneTxnMultiDeniedApprovalError, dispatch]);

  return (
    <>
      <div className="standard-card-alignment gap-x-gap-sm gap-y-gap-md">
        {data?.map((account) => (
          <CardContainer
            hoverHeight={
              GlobalConfiguration?.reimbursementTxnMultiApprovalCardHoverHeight
            }
            filterRef={filterRef}
            isDisabled={
              filterOpen ||
              account.beneficiary_account_approval_status === "pending" ||
              account.beneficiary_account_approval_status === "rejected"
            }
            key={account?.request_id}
            isSelected={selectedTransactions.includes(account.request_id)}
            onClickHandler={() => handleCardClick(account)}
            varaint="overlay"
          >
            <card-body>
              <TextWithIcon
                type={
                  account.beneficiary_account_approval_status === "pending" ||
                  account.beneficiary_account_approval_status === "rejected"
                    ? "Text"
                    : "Amount"
                }
                fontWeight={"semibold"}
                text={
                  account.beneficiary_account_approval_status === "pending"
                    ? "Account approval pending"
                    : account.beneficiary_account_approval_status === "rejected"
                      ? "Account approval rejected"
                      : account?.requested_amount
                }
                textColor={getStatusColor(
                  account.beneficiary_account_approval_status,
                )}
                subText={
                  account.beneficiary_account_approval_status === "pending" ||
                  account.beneficiary_account_approval_status === "rejected"
                    ? "Account status"
                    : "Amount"
                }
                iconComponentArray={
                  account.beneficiary_account_approval_status === "approved"
                    ? [
                        ...(account?.confidential_transaction === "Y"
                          ? [
                              {
                                icon: ICONS?.CONFIDENTIAL_TRANSACTION,
                                title: "confidential",
                                isDisabled: false,
                              },
                            ]
                          : []),
                        {
                          icon: selectedTransactions.includes(
                            account?.request_id,
                          )
                            ? ICONS?.SELECTED_WITH_GREEN_CIRCLE
                            : ICONS?.SELECT_ALL_WITH_WHITE_OUTLINE_CIRCLE,
                          isDisabled: false,
                        },
                      ]
                    : []
                }
              />
              <TextWithIcon
                type={"Text"}
                fontWeight={"var(--font-normal)"}
                text={account.beneficiary_account_name}
                textColor={"var(--text-primary)"}
                subText="Account name"
              />

              <TextWithCTA
                btnText={
                  account.beneficiary_account_approval_status === "pending" ||
                  account.beneficiary_account_approval_status === "rejected"
                    ? "Approve account"
                    : "More details"
                }
                enabledBgColor={"var(--text-primary)"}
                enabledTextColor={"var(--primary-bg)"}
                handleFunction={(e) => handleCtaClick(e, account)}
                icon={ICONS?.ARROW_RIGHT_WHITE}
                isDisabled={!!filterOpen}
                text={
                  account.created_on
                    ? formatDateViewAll(account.created_on, false, false)
                    : "Not given"
                }
                subText="Date"
                type={"Text"}
              />
            </card-body>
            {/* <card-hover>
              <TextWithIcon
                text={account?.labels?.join(", ") || "No labels assigned "}
                fontWeight={"normal"}
                textColor={"var(--text-primary)"}
                iconComponentArray={[
                  {
                    icon: ICONS?.LABEL,
                  },
                ]}
              />
            </card-hover> */}
          </CardContainer>
        ))}
      </div>
    </>
  );
};

export default ReimbursementRequestTxnMultiCard;
